import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {  Tooltip, 
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Chip,
  ButtonGroup,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  CalendarToday,
  Print,
  Close,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { appointmentService, patientService, userService, authService, prescriptionService } from '../../services/api';
import TopBar from '../../components/TopBar';

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Consume the incoming patientId exactly once — useRef so patients re-loading never re-triggers it
  const openWithPatientIdRef = useRef(location.state?.openNewWithPatientId ?? null);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateFilter, setDateFilter] = useState('today');
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    appointmentDate: '',
    appointmentTime: '',
    type: 'OPD',
    notes: '',
    status: 'Scheduled',
    bp: '',
    pulse: '',
    weight: '',
    spO2: '',
    gestationalAge: '',
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // If navigated here from duplicate-patient dialog, auto-open new appointment with patient pre-selected.
  // Uses a ref so this fires only once — subsequent patients reloads (after save) won't re-open the dialog.
  useEffect(() => {
    const patientId = openWithPatientIdRef.current;
    if (!patientId || patients.length === 0) return;

    openWithPatientIdRef.current = null; // consume — never fires again for this navigation

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    setEditingAppointment(null);
    setFormData({
      patientId: String(patientId),
      doctorId: '',
      appointmentDate: todayStr,
      appointmentTime: currentTime,
      type: 'OPD',
      notes: '',
      status: 'Scheduled',
      bp: '',
      pulse: '',
      weight: '',
      spO2: '',
      gestationalAge: '',
    });
    setOpenDialog(true);
  }, [patients]);

  const loadData = async () => {
    try {
      const [appointmentsRes, patientsRes, doctorsRes] = await Promise.all([
        appointmentService.getAllAppointments(),
        patientService.getAllPatients(),
        userService.getDoctors(),
      ]);
      
      console.log('=== LOAD DATA DEBUG ===');
      console.log('RAW API Response:', appointmentsRes);
      console.log('Response.data:', appointmentsRes.data);
      console.log('Response.data type:', typeof appointmentsRes.data);
      console.log('Response.data is Array:', Array.isArray(appointmentsRes.data));
      console.log('API Response - appointmentsRes.data:', appointmentsRes.data);
      console.log('First appointment from API:', appointmentsRes.data[0]);
      if (appointmentsRes.data[0]) {
        console.log('First appointment keys:', Object.keys(appointmentsRes.data[0]));
        console.log('First appointment SPO2:', appointmentsRes.data[0].spO2);
        console.log('First appointment SPO2 (uppercase):', appointmentsRes.data[0].SPO2);
        console.log('First appointment["spO2"]:', appointmentsRes.data[0]["spO2"]);
        console.log('First appointment vitals:', {
          bp: appointmentsRes.data[0].bp,
          pulse: appointmentsRes.data[0].pulse,
          weight: appointmentsRes.data[0].weight,
          spo2: appointmentsRes.data[0].spO2,
          SPO2: appointmentsRes.data[0].SPO2
        });
      }
      
      setAppointments(appointmentsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      
      console.log('State updated. appointments state will have:', appointmentsRes.data.length, 'appointments');
      console.log('=== END LOAD DATA DEBUG ===');
    } catch (err) {
      setError('Failed to load data');
      console.error('Load data error:', err);
    }
  };

  const getDateForFilter = (filterType) => {
    const today = new Date();
    // Reset time to midnight to avoid timezone issues
    today.setHours(0, 0, 0, 0);
    
    if (filterType === 'yesterday') {
      today.setDate(today.getDate() - 1);
    } else if (filterType === 'tomorrow') {
      today.setDate(today.getDate() + 1);
    }
    // 'today' keeps current date
    
    return today;
  };

  const handleDateFilter = (filterType) => {
    setDateFilter(filterType);
    setSelectedDate(getDateForFilter(filterType));
  };

  const formatDateForFilter = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const filteredAppointments = appointments.filter((apt) => {
    if (!apt.appointmentDate) return false;
    const aptDateStr = apt.appointmentDate.split('T')[0];
    const filterDateStr = formatDateForFilter(selectedDate);
    if (aptDateStr !== filterDateStr) return false;
    if (statusFilter && apt.status !== statusFilter) return false;
    return true;
  });

  const handleOpenDialog = (appointmentOrId = null) => {
    if (appointmentOrId) {
      // Edit mode - get full appointment from state, not from DataGrid params.row
      const appointmentId = typeof appointmentOrId === 'object' ? appointmentOrId.id : appointmentOrId;
      const appointment = appointments.find(apt => apt.id === appointmentId);
      
      if (!appointment) {
        console.error('Appointment not found in state:', appointmentId);
        return;
      }
      
      console.log('Opening edit dialog for appointment:', appointment);
      console.log('SPO2 value:', appointment.spO2);
      console.log('SPO2 type:', typeof appointment.spO2);
      console.log('SPO2 uppercase:', appointment.SPO2);
      console.log('SPO2 uppercase type:', typeof appointment.SPO2);
      console.log('All vitals:', {
        bp: appointment.bp,
        pulse: appointment.pulse,
        weight: appointment.weight,
        spo2: appointment.spO2,
        SPO2: appointment.SPO2
      });
      
      // Explicit SPO2 value extraction
      const spo2Value = appointment.spO2 || appointment.SPO2 || '';
      console.log('Calculated SPO2 value for formData:', spo2Value);
      console.log('Calculated SPO2 type:', typeof spo2Value);
      
      setEditingAppointment(appointment);
      setFormData({
        patientId: appointment.patientId,
        doctorId: appointment.doctorId,
        appointmentDate: appointment.appointmentDate ? appointment.appointmentDate.split('T')[0] : '',
        appointmentTime: appointment.appointmentTime || '',
        type: appointment.type || 'OPD',
        notes: appointment.notes || '',
        status: appointment.status || 'Scheduled',
        bp: appointment.bp || '',
        pulse: appointment.pulse || '',
        weight: appointment.weight || '',
        spo2: spo2Value,
        gestationalAge: appointment.gestationalAge || '',
      });
      
      console.log('FormData set with spo2:', spo2Value);
    } else {
      // Create mode
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setEditingAppointment(null);
      setFormData({
        patientId: '',
        doctorId: '',
        appointmentDate: formatDateForFilter(new Date()),
        appointmentTime: currentTime,
        type: 'OPD',
        notes: '',
        status: 'Scheduled',
        bp: '',
        pulse: '',
        weight: '',
        spO2: '',
        gestationalAge: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAppointment(null);
    setFormData({
      patientId: '',
      doctorId: '',
      appointmentDate: '',
      appointmentTime: '',
      type: 'OPD',
      notes: '',
      status: 'Scheduled',
      bp: '',
      pulse: '',
      weight: '',
      spO2: '',
      gestationalAge: '',
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateGestationalAge = (lmp) => {
    if (!lmp) return '';
    
    const lmpDate = new Date(lmp);
    const today = new Date();
    const diffTime = Math.abs(today - lmpDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;
    
    return `${weeks}w ${days}d`;
  };

  const handlePatientChange = (patient) => {
    setFormData({
      ...formData,
      patientId: patient ? patient.id : '',
      gestationalAge: patient?.lmp ? calculateGestationalAge(patient.lmp) : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        patientId: parseInt(formData.patientId),
        doctorId: parseInt(formData.doctorId),
        bp: formData.bp || null,
        pulse: formData.pulse || null,
        weight: formData.weight || null,
        spo2: formData.spO2 || null,
        gestationalAge: formData.gestationalAge || null,
      };

      if (editingAppointment) {
        payload.id = editingAppointment.id;
        await appointmentService.updateAppointment(editingAppointment.id, payload);
        setSuccess('Appointment updated successfully!');
      } else {
        await appointmentService.createAppointment(payload);
        setSuccess('Appointment created successfully!');
      }

      handleCloseDialog();
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save appointment');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await appointmentService.deleteAppointment(id);
        setSuccess('Appointment deleted successfully!');
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete appointment');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handlePrintPrescription = async (appointmentId) => {
    try {
      const res = await prescriptionService.getByAppointmentId(appointmentId);
      navigate(`/prescription/${res.data.id}/print`);
    } catch {
      setError('No prescription found for this appointment.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      width: 180,
      valueGetter: (params) => {
        const patient = params.row.patient;
        return patient ? `${patient.firstName} ${patient.lastName}` : '-';
      },
    },
    {
      field: 'doctorName',
      headerName: 'Doctor Name',
      width: 180,
      valueGetter: (params) => {
        const doctor = params.row.doctor;
        return doctor ? `Dr. ${doctor.firstName} ${doctor.lastName}` : '-';
      },
    },
    {
      field: 'appointmentDate',
      headerName: 'Date',
      width: 120,
      valueGetter: (params) =>
        params.row.appointmentDate
          ? new Date(params.row.appointmentDate).toLocaleDateString('en-IN')
          : '-',
    },
    { field: 'appointmentTime', headerName: 'Time', width: 100 },
    { field: 'type', headerName: 'Type', width: 100 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Appointment">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Appointment">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'Completed' ? 'Print Prescription' : 'Prescription available after appointment is completed'}>
            <span>
              <IconButton
                size="small"
                color="secondary"
                disabled={params.row.status !== 'Completed'}
                onClick={() => handlePrintPrescription(params.row.id)}
              >
                <Print />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Appointments" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              All Appointments
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              }}
            >
              New Appointment
            </Button>
          </Box>

          {/* Combined Filter Row */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
            <CalendarToday sx={{ color: 'primary.main', flexShrink: 0 }} />
            <TextField
              type="date"
              label="Select Date"
              value={formatDateForFilter(selectedDate)}
              onChange={(e) => {
                setSelectedDate(new Date(e.target.value));
                setDateFilter('custom');
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: '160px', flexShrink: 0 }}
            />
            <ButtonGroup variant="outlined" size="small">
              <Button
                onClick={() => handleDateFilter('yesterday')}
                variant={dateFilter === 'yesterday' ? 'contained' : 'outlined'}
              >
                Yesterday
              </Button>
              <Button
                onClick={() => handleDateFilter('today')}
                variant={dateFilter === 'today' ? 'contained' : 'outlined'}
              >
                Today
              </Button>
              <Button
                onClick={() => handleDateFilter('tomorrow')}
                variant={dateFilter === 'tomorrow' ? 'contained' : 'outlined'}
              >
                Tomorrow
              </Button>
            </ButtonGroup>

            <ButtonGroup variant="outlined" size="small">
              <Button
                onClick={() => setStatusFilter(statusFilter === 'Scheduled' ? '' : 'Scheduled')}
                variant={statusFilter === 'Scheduled' ? 'contained' : 'outlined'}
                color="primary"
              >
                Scheduled
              </Button>
              <Button
                onClick={() => setStatusFilter(statusFilter === 'Completed' ? '' : 'Completed')}
                variant={statusFilter === 'Completed' ? 'contained' : 'outlined'}
                color="success"
              >
                Completed
              </Button>
              <Button
                onClick={() => setStatusFilter(statusFilter === 'Cancelled' ? '' : 'Cancelled')}
                variant={statusFilter === 'Cancelled' ? 'contained' : 'outlined'}
                color="error"
              >
                Cancelled
              </Button>
            </ButtonGroup>

            {statusFilter && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<Close fontSize="small" />}
                onClick={() => setStatusFilter('')}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                Reset
              </Button>
            )}
          </Box>

          <DataGrid
            rows={filteredAppointments}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Create/Edit Appointment Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        key={editingAppointment ? editingAppointment.id : 'new'}
      >
        <DialogTitle>
          {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={patients}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    return options.filter((p) =>
                      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
                      (p.phoneNumber || '').toLowerCase().includes(q)
                    );
                  }}
                  value={patients.find((p) => p.id === parseInt(formData.patientId)) || null}
                  onChange={(_, newValue) => handlePatientChange(newValue)}
                  disabled={!!editingAppointment}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.firstName} {option.lastName}
                        </Typography>
                        {option.phoneNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {option.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patient"
                      required
                      helperText={editingAppointment ? 'Cannot change patient' : 'Type name or phone to search'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Doctor"
                  name="doctorId"
                  value={formData.doctorId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">Select Doctor</MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.firstName} {doctor.lastName}
                      {doctor.specialization && ` - ${doctor.specialization}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Appointment Date"
                  name="appointmentDate"
                  type="date"
                  value={formData.appointmentDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Appointment Time"
                  name="appointmentTime"
                  type="time"
                  value={formData.appointmentTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled
                  helperText={editingAppointment ? 'Type cannot be changed' : 'Only OPD appointments can be raised here'}
                >
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="IPD">IPD</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={!editingAppointment}
                  helperText={!editingAppointment ? "Status can only be changed when editing" : ""}
                >
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Chief Complaint"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  disabled={!!editingAppointment}
                  helperText={editingAppointment ? "Cannot change chief complaint" : ""}
                />
              </Grid>
              
              {/* Vital Signs Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'primary.main' }}>
                  Vital Signs
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="BP (Blood Pressure)"
                  name="bp"
                  value={formData.bp}
                  onChange={handleChange}
                  placeholder="e.g., 120/80"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pulse"
                  name="pulse"
                  value={formData.pulse}
                  onChange={handleChange}
                  placeholder="e.g., 72"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  placeholder="e.g., 65"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SPO2 (%)"
                  name="spO2"
                  value={formData.spO2}
                  onChange={handleChange}
                  placeholder="e.g., 98"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Age"
                  value={formData.patientId ? (() => {
                    const patient = patients.find(p => p.id === parseInt(formData.patientId));
                    return patient ? calculateAge(patient.dateOfBirth) + ' years' : '';
                  })() : ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Auto-calculated from patient's date of birth"
                  disabled
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gestational Age"
                  name="gestationalAge"
                  value={formData.gestationalAge}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Auto-calculated from patient's LMP"
                  disabled
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingAppointment ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
